import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const ManagePayouts = () => {
    const [payoutRequests, setPayoutRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Pagination
    const [pagination, setPagination] = useState({});
    const [page, setPage] = useState(1);

    const [approvalModal, setApprovalModal] = useState({ show: false, request: null, transactionId: '', adminNotes: '' });
    const [rejectionModal, setRejectionModal] = useState({ show: false, request: null, adminNotes: '' });
    const [editModal, setEditModal] = useState({ show: false, request: null, transactionId: '', adminNotes: '' });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // ⭐ Debounce search input
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search);
        }, 300);
        return () => clearTimeout(handler);
    }, [search]);

    // Load payout requests when filter, search, or page changes
    useEffect(() => {
        loadPayoutRequests(1); // reset to first page on filter/search change
    }, [statusFilter, debouncedSearch]);

    const loadPayoutRequests = async (pageNumber = 1) => {
        setIsLoading(true);
        try {
            const params = { page: pageNumber };
            if (statusFilter !== 'all') params.status = statusFilter;
            if (debouncedSearch.trim() !== '') params.search = debouncedSearch;

            const response = await api.get('/admin/financial/payout-requests', { params });

            if (response.data.status === 'success') {
                const data = response.data.data;
                setPayoutRequests(data.data);
                setPagination(data);
                setPage(pageNumber);
            }
            return true; // ✅ resolve promise when done
        } catch (error) {
            console.error(error);
            setPayoutRequests([]);
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    // Pagination component
    const renderPagination = () => {
        if (!pagination.last_page || pagination.last_page <= 1) return null;

        const current = pagination.current_page;
        const last = pagination.last_page;
        const maxPages = 3;

        let start = Math.max(1, current - Math.floor(maxPages / 2));
        let end = start + maxPages - 1;

        if (end > last) {
            end = last;
            start = Math.max(1, end - maxPages + 1);
        }

        const pageNumbers = [];
        for (let i = start; i <= end; i++) pageNumbers.push(i);

        return (
            <nav className="mt-4">
                <ul className="pagination justify-content-center">
                    <li className={`page-item ${current === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => loadPayoutRequests(current - 1)}>Previous</button>
                    </li>

                    {start > 1 && (
                        <>
                            <li className="page-item"><button className="page-link" onClick={() => loadPayoutRequests(1)}>1</button></li>
                            {start > 2 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                        </>
                    )}

                    {pageNumbers.map(num => (
                        <li key={num} className={`page-item ${num === current ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => loadPayoutRequests(num)}>{num}</button>
                        </li>
                    ))}

                    {end < last && (
                        <>
                            {end < last - 1 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                            <li className="page-item"><button className="page-link" onClick={() => loadPayoutRequests(last)}>{last}</button></li>
                        </>
                    )}

                    <li className={`page-item ${current === last ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => loadPayoutRequests(current + 1)}>Next</button>
                    </li>
                </ul>
            </nav>
        );
    };

    // Formatters
    const formatAmount = (amount, currency = 'QAR') => {
        const formattedNumber = parseFloat(amount || 0).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });

        switch (currency) {
            case 'USD':
                return `$${formattedNumber}`;
            case 'EUR':
                return `€${formattedNumber}`;
            case 'AED':
                return `AED ${formattedNumber}`;
            default:
                return `${currency} ${formattedNumber}`; // use the actual currency
        }
    };


    const handleReject = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!rejectionModal.request) return;

        try {
            const response = await api.post(
                `/admin/financial/payout-requests/${rejectionModal.request.id}/reject`,
                {
                    admin_notes: rejectionModal.adminNotes
                }
            );

            if (response.data.status === 'success') {
                // Close modal

                // Reload the page
            }
            setRejectionModal({ show: false, request: null, adminNotes: '' });
            loadPayoutRequests(page);

            // Show success alert (optional)
            setSuccess('Payout request rejected successfully');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to reject payout request');
        }
    };

    const openRejectionModal = (request) => {
        setRejectionModal({ show: true, request, adminNotes: '' });
        setError('');
        setSuccess('');
    };

    const openEditModal = (request) => {
        setEditModal({
            show: true,
            request,
            transactionId: request.transaction_bank_id || '',
            adminNotes: request.admin_notes || ''
        });
        setError('');
        setSuccess('');
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!editModal.request) return;

        try {
            const response = await api.post(
                `/admin/financial/payout-requests/${editModal.request.id}/approve`,
                {
                    transaction_bank_id: editModal.transactionId,
                    admin_notes: editModal.adminNotes,
                }
            );

            if (response.data.status === 'success') {
                // Close modal
                // Reload the page
            }
            setSuccess('Payout request updated and approved successfully');
            setEditModal({ show: false, request: null, transactionId: '', adminNotes: '' });
            loadPayoutRequests(page);

            // Show success alert (optional)

        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update payout request');
        }
    };


    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'dumped':
                return 'badge-success';
            case 'undumped':
                return 'badge-danger';
            default:
                return 'badge-warning';
        }
    };

    const getPendingCount = () => payoutRequests.filter(r => r.status === 'pending').length;

    return (
        <div className="container-fluid mt-4">
            <div className="d-flex text-gray-900 justify-content-between align-items-center mb-4">
                <h2 className="font-weight-bold">
                    Manage Payout Requests
                    {getPendingCount() > 0 && (
                        <span className="badge badge-primary ml-2">{getPendingCount()} Pending</span>
                    )}
                </h2>
            </div>

            {/* Success/Error Messages */}
            {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    {error}
                    <button type="button" className="close" onClick={() => setError('')}>
                        <span>&times;</span>
                    </button>
                </div>
            )}

            {success && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                    {success}
                    <button type="button" className="close" onClick={() => setSuccess('')}>
                        <span>&times;</span>
                    </button>
                </div>
            )}

            <div className="card shadow mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by order number, customer name or email..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}   // ⭐ LIVE SEARCH
                            />
                        </div>

                        <div className="col-md-3">
                            <select
                                className="form-control"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="all">All Requests</option>
                                <option value="pending">Pending</option>
                                <option value="dumped">Approved</option>
                                <option value="undumped">Rejected</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="card">
                <div className="card-body">
                    {isLoading ? (
                        <div className="text-center text-primary py-5">
                            <div className="spinner-border" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                        </div>
                    ) : payoutRequests.length === 0 ? (
                        <p className="text-center text-muted py-5">No payout requests found</p>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead>
                                <tr>
                                    <th>Request Date</th>
                                    <th>Organizer</th>
                                    <th>Amount</th>
                                    <th>Event Name</th>
                                    <th>Bank Account Number</th>
                                    <th>Bank Account Currency</th>
                                    <th>IBAN</th>
                                    <th>Status</th>
                                    <th>Processed At</th>
                                    <th>Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {payoutRequests.map((request) => (
                                    <tr key={request.id}>
                                        <td>{new Date(request.requested_at).toLocaleDateString()}</td>
                                        <td>
                                            <div>{request.organizer_name}</div>
                                            <small className="text-muted">{request.organizer_email}</small>
                                        </td>
                                        <td className="font-weight-bold">
                                            {formatAmount(request.requested_amount, request.currency)}
                                        </td>
                                        <td>{request.event_name || <span className="text-muted">-</span>}</td>
                                        <td>{request.bank_account_number || <span className="text-muted">-</span>}</td>
                                        <td>{request.bank_account_currency || <span className="text-muted">-</span>}</td>
                                        <td>{request.iban || <span className="text-muted">-</span>}</td>
                                        <td>
                        <span className={`badge ${getStatusBadgeClass(request.status)}`}>
                          {request.status.toUpperCase()}
                        </span>
                                        </td>
                                        <td>{request.processed_at ? new Date(request.processed_at).toLocaleDateString() : '-'}</td>
                                        <td>
                                            {request.status === 'pending' && (
                                                <div className="flex gap-2">
                                                    <button
                                                        className="btn-outline-success"
                                                        onClick={() => openEditModal(request)}
                                                        title="Edit & Approve"
                                                    >
                                                        <i className="fas fa-check"></i>
                                                    </button>
                                                    <button
                                                        className="btn-outline-danger"
                                                        onClick={() => openRejectionModal(request)}
                                                        title="Reject"
                                                    >
                                                        <i className="fas fa-times"></i>
                                                    </button>
                                                </div>
                                            )}

                                            {request.status === 'approved' && (
                                                <span className="badge badge-success">Processed</span>
                                            )}

                                            {request.status === 'rejected' && (
                                                <span className="badge badge-danger">Rejected</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                            {renderPagination()}
                        </div>
                    )}
                </div>
            </div>

            {/* Edit Modal */}
            {editModal.show && (
                <div className="modal" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Edit & Approve Payout Request</h5>
                                <button
                                    type="button"
                                    className="close"
                                    onClick={() =>
                                        setEditModal({ show: false, request: null, transactionId: '', adminNotes: '' })
                                    }
                                >
                                    <span>&times;</span>
                                </button>
                            </div>
                            <form onSubmit={handleEditSubmit}>
                                <div className="modal-body">
                                    <p><strong>Organizer:</strong> {editModal.request?.organizer_name}</p>
                                    <p><strong>Email:</strong> {editModal.request?.organizer_email}</p>
                                    <p><strong>Amount:</strong> {formatAmount(editModal.request?.requested_amount, editModal.request?.currency)}</p>

                                    <div className="form-group">
                                        <label htmlFor="editTransactionId">Bank Transaction ID *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="editTransactionId"
                                            value={editModal.transactionId}
                                            onChange={(e) =>
                                                setEditModal({ ...editModal, transactionId: e.target.value })
                                            }
                                            placeholder="Enter bank transaction reference"
                                            required
                                        />
                                        <small className="form-text text-muted">
                                            This will be provided to the organizer as proof of payment
                                        </small>
                                    </div>

                                    {/*<div className="alert alert-info">*/}
                                    {/*    <small>*/}
                                    {/*        <strong>Info:</strong> After approval, this request will show your name as "Approved By"*/}
                                    {/*        and the transaction ID will be recorded.*/}
                                    {/*    </small>*/}
                                    {/*</div>*/}
                                </div>
                                <div className="modal-footer">
                                    <button type="submit" className="btn-outline-success">
                                        Approve & Save
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}


            {/* Rejection Modal */}
            {rejectionModal.show && (
                <div className="modal" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Reject Payout Request</h5>
                                <button type="button" className="close" onClick={() => setRejectionModal({ show: false, request: null, adminNotes: '' })}>
                                    <span>&times;</span>
                                </button>
                            </div>
                            <form onSubmit={handleReject}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <p><strong>Organizer:</strong> {rejectionModal.request?.organizer_name}</p>
                                        <p><strong>Amount:</strong> {formatAmount(rejectionModal.request?.amount, rejectionModal.request?.currency)}</p>
                                        {rejectionModal.request?.organizer_notes && (
                                            <p><strong>Organizer Notes:</strong> {rejectionModal.request.organizer_notes}</p>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="rejectionNotes">Reason for Rejection *</label>
                                        <textarea
                                            className="form-control"
                                            id="rejectionNotes"
                                            rows="4"
                                            value={rejectionModal.adminNotes}
                                            onChange={(e) => setRejectionModal({ ...rejectionModal, adminNotes: e.target.value })}
                                            placeholder="Explain why this payout request is being rejected..."
                                            required
                                        ></textarea>
                                        <small className="form-text text-muted">
                                            This message will be visible to the organizer
                                        </small>
                                    </div>

                                    <div className="alert alert-danger">
                                        <small>
                                            <strong>Note:</strong> The organizer will be notified of the rejection
                                            and can submit a new request if needed.
                                        </small>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="submit" className="btn-outline-danger">
                                        Reject Request
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManagePayouts;
